import { createTheme } from '@mui/material/styles';

const BRIGHT_GREEN = '#9FE870';
const FOREST_GREEN = '#163300';
const WHITE = '#FFFFFF';
const DARK_BG = '#121511';
const CONTENT_PRIMARY_LIGHT = '#0E0F0C';
const CONTENT_SECONDARY_LIGHT = '#454745';
const BORDER_LIGHT = 'rgba(14, 15, 12, 0.12)';
const SECTION_BG_LIGHT = 'rgba(22, 51, 0, 0.08)';

export const getThemeOptions = (mode) => {
  const isLight = mode === 'light';

  return {
    palette: {
      mode,
      primary: {
        main: BRIGHT_GREEN,
        light: '#B6F192',
        dark: '#8FD760',
        contrastText: FOREST_GREEN,
      },
      secondary: {
        main: isLight ? FOREST_GREEN : BRIGHT_GREEN,
        light: isLight ? '#2A4D00' : '#B6F192',
        dark: isLight ? '#0B1A00' : '#8FD760',
        contrastText: isLight ? WHITE : FOREST_GREEN,
      },
      error: {
        main: '#E53935',
        contrastText: WHITE,
      },
      warning: {
        main: '#FFEB69',
        contrastText: FOREST_GREEN,
      },
      info: {
        main: '#163300',
        contrastText: WHITE,
      },
      success: {
        main: BRIGHT_GREEN,
        light: '#B6F192',
        dark: FOREST_GREEN,
        contrastText: FOREST_GREEN,
      },
      background: {
        default: isLight ? WHITE : DARK_BG,
        paper: isLight ? WHITE : '#1C1F1A',
        neutral: isLight ? SECTION_BG_LIGHT : 'rgba(159, 232, 112, 0.05)',
      },
      text: {
        primary: isLight ? CONTENT_PRIMARY_LIGHT : WHITE,
        secondary: isLight ? CONTENT_SECONDARY_LIGHT : 'rgba(255, 255, 255, 0.7)',
      },
      divider: isLight ? BORDER_LIGHT : 'rgba(255, 255, 255, 0.1)',
      action: {
        active: isLight ? FOREST_GREEN : BRIGHT_GREEN,
        hover: isLight ? 'rgba(22, 51, 0, 0.04)' : 'rgba(159, 232, 112, 0.04)',
        selected: isLight ? 'rgba(22, 51, 0, 0.08)' : 'rgba(159, 232, 112, 0.08)',
        disabled: isLight ? 'rgba(0, 0, 0, 0.26)' : 'rgba(255, 255, 255, 0.3)',
        disabledBackground: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
      },
      brand: {
        brightGreen: BRIGHT_GREEN,
        forestGreen: FOREST_GREEN,
      },
      secondaryBrand: {
        orange: '#FFC091',
        blue: '#A5D8FF',
        purple: '#D0BFFF',
        pink: '#FFD3E0',
      },
    },
    shape: {
      borderRadius: 12,
    },
    sidebar: {
      bg: FOREST_GREEN,
      text: WHITE,
      textSecondary: 'rgba(255, 255, 255, 0.65)',
      activeItemBg: BRIGHT_GREEN,
      activeItemText: FOREST_GREEN,
      hoverItemBg: 'rgba(159, 232, 112, 0.12)',
      divider: 'rgba(255, 255, 255, 0.12)',
      icon: 'rgba(255, 255, 255, 0.75)',
      activeIcon: FOREST_GREEN,
    },
    gradients: {
      primary: `linear-gradient(135deg, ${BRIGHT_GREEN} 0%, #8FD760 100%)`,
      secondary: `linear-gradient(135deg, ${FOREST_GREEN} 0%, #2A4D00 100%)`,
      success: `linear-gradient(135deg, #9FE870 0%, #8FD760 100%)`,
      error: `linear-gradient(135deg, #E53935 0%, #D32F2F 100%)`,
    },
    typography: {
      fontFamily: '"Inter", "Outfit", sans-serif',
      h1: {
        fontFamily: '"Wise Sans", "Outfit", sans-serif',
        fontSize: '2.5rem',
        fontWeight: 800,
        color: isLight ? FOREST_GREEN : WHITE,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontFamily: '"Wise Sans", "Outfit", sans-serif',
        fontSize: '2rem',
        fontWeight: 700,
        color: isLight ? FOREST_GREEN : WHITE,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontFamily: '"Wise Sans", "Outfit", sans-serif',
        fontSize: '1.5rem',
        fontWeight: 700,
        color: isLight ? FOREST_GREEN : WHITE,
      },
      h4: {
        fontFamily: '"Wise Sans", "Outfit", sans-serif',
        fontSize: '1.25rem',
        fontWeight: 700,
        color: isLight ? FOREST_GREEN : WHITE,
      },
      h5: {
        fontFamily: '"Wise Sans", "Outfit", sans-serif',
        fontSize: '1.125rem',
        fontWeight: 600,
        color: isLight ? CONTENT_PRIMARY_LIGHT : WHITE,
      },
      h6: {
        fontFamily: '"Wise Sans", "Outfit", sans-serif',
        fontSize: '1rem',
        fontWeight: 600,
        color: isLight ? CONTENT_PRIMARY_LIGHT : WHITE,
      },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.5 },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
      },
      summaryNumber: {
        fontFamily: '"Wise Sans", "Outfit", sans-serif',
        fontWeight: 800,
        color: isLight ? FOREST_GREEN : BRIGHT_GREEN, 
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isLight ? WHITE : DARK_BG,
            color: isLight ? CONTENT_PRIMARY_LIGHT : WHITE,
            margin: 0,
            padding: 0,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 20px',
            '&:focus-visible': {
              outline: 'none',
              boxShadow: `0 0 0 2px ${isLight ? WHITE : DARK_BG}, 0 0 0 4px ${isLight ? FOREST_GREEN : BRIGHT_GREEN}`,
            },
          },
          containedPrimary: {
            backgroundColor: BRIGHT_GREEN,
            color: FOREST_GREEN,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#8FD760',
              boxShadow: 'none',
            },
          },
          outlinedSecondary: {
            borderColor: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            color: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            borderWidth: 2,
            backgroundColor: 'transparent',
            '&:hover': {
              borderWidth: 2,
              backgroundColor: isLight ? 'rgba(22, 51, 0, 0.04)' : 'rgba(159, 232, 112, 0.04)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '& fieldset': {
                borderColor: isLight ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: isLight ? FOREST_GREEN : BRIGHT_GREEN,
              },
              '&.Mui-focused fieldset': {
                borderColor: isLight ? FOREST_GREEN : BRIGHT_GREEN,
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
              color: isLight ? '#0B1A00' : '#8FD760',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: isLight ? `1px solid ${BORDER_LIGHT}` : '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: 'none',
            backgroundColor: isLight ? WHITE : '#1C1F1A',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
          },
          head: {
            backgroundColor: isLight ? 'rgba(22, 51, 0, 0.04)' : 'rgba(159, 232, 112, 0.05)',
            color: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            fontWeight: 700,
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backgroundImage: 'none',
          },
          elevation1: {
            border: isLight ? `1px solid ${BORDER_LIGHT}` : '1px solid rgba(255, 255, 255, 0.1)',
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? WHITE : DARK_BG,
            color: isLight ? CONTENT_PRIMARY_LIGHT : WHITE,
            boxShadow: `0 1px 0 ${isLight ? BORDER_LIGHT : 'rgba(255, 255, 255, 0.1)'}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: FOREST_GREEN,
            borderRight: 'none',
            boxShadow: '2px 0 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.925rem',
          },
          standardSuccess: {
            color: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            backgroundColor: isLight ? 'rgba(159, 232, 112, 0.15)' : 'rgba(159, 232, 112, 0.08)',
            '& .MuiAlert-icon': {
              color: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            },
          },
          outlinedSuccess: {
            color: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            borderColor: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            '& .MuiAlert-icon': {
              color: isLight ? FOREST_GREEN : BRIGHT_GREEN,
            },
          },
          filledSuccess: {
            backgroundColor: BRIGHT_GREEN,
            color: FOREST_GREEN,
            '& .MuiAlert-icon': {
              color: FOREST_GREEN,
            },
          },
          filledError: {
            backgroundColor: '#E53935',
            color: WHITE,
          },
          filledWarning: {
            backgroundColor: '#FFEB69',
            color: FOREST_GREEN,
          },
          filledInfo: {
            backgroundColor: '#163300',
            color: WHITE,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 600,
          },
          filledPrimary: {
            backgroundColor: BRIGHT_GREEN,
            color: FOREST_GREEN,
          },
          filledSecondary: {
            backgroundColor: FOREST_GREEN,
            color: WHITE,
          },
          filledError: {
            backgroundColor: '#E53935',
            color: WHITE,
          },
          outlinedPrimary: {
            borderColor: BRIGHT_GREEN,
            color: isLight ? FOREST_GREEN : BRIGHT_GREEN,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            // Ensure icons are visible in dark mode by default
            color: isLight ? 'inherit' : 'rgba(255, 255, 255, 0.85)', 
          }
        }
      }
    },
  };
};

export const getTheme = (mode) => createTheme(getThemeOptions(mode));

const theme = getTheme('light');
export default theme;
